import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, withCredentials: true });

export const TOKEN_KEY = "attire.token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));

client.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});
client.interceptors.response.use(
  (r) => {
    // Live Threadworth: any write, or a bulk-import status tick, nudges every subscriber to refetch + animate.
    const m = (r.config?.method || "get").toLowerCase();
    const u = String(r.config?.url || "");
    if ((m !== "get" && !u.startsWith("/auth/") && !u.includes("/notifications")) || u.includes("/bulk/")) {
      window.dispatchEvent(new Event("threadlist:worth:raw"));
    }
    return r;
  },
  (err) => {
    if (err?.response?.status === 401 && !String(err.config?.url || "").startsWith("/auth/")) {
      window.dispatchEvent(new CustomEvent("attire:unauthorized"));
    }
    return Promise.reject(err);
  }
);

// Image URLs: the auth cookie rides along with <img> requests; token query is a fallback.
const tq = () => { const t = getToken(); return t ? `token=${encodeURIComponent(t)}` : ""; };
const withTok = (url) => { const q = tq(); return q ? `${url}${url.includes("?") ? "&" : "?"}${q}` : url; };
// `v` busts the browser cache whenever an item's photos change (we pass updated_at).
export const imageUrl = (id, kind, v) => withTok(`${API}/items/${id}/image/${kind}${v ? `?v=${encodeURIComponent(v)}` : ""}`);

// Prefer enhanced image if available, else front. Other people's items use the public route.
export const itemImage = (item) => {
  if (!item) return "";
  const v = item.updated_at || item.created_at || "";
  if (item.owner_id && window.__attireUserId && item.owner_id !== window.__attireUserId) {
    return `${API}/public/items/${item.id}/image/${item.has_enhanced ? "enhanced" : "front"}?v=${encodeURIComponent(v)}`;
  }
  return imageUrl(item.id, item.has_enhanced ? "enhanced" : "front", v);
};
export const publicItemImage = (item) => `${API}/public/items/${item.id}/image/${item.has_enhanced ? "enhanced" : "front"}`;
export const postImage = (postId) => `${API}/public/posts/${postId}/image`;
export const avatarUrl = (handle) => `${API}/public/users/${handle}/avatar`;

export const api = {
  // items
  createItem: (payload) => client.post("/items", payload, { timeout: 180000 }).then((r) => r.data),
  listItems: (params) => client.get("/items", { params }).then((r) => r.data),
  getItem: (id) => client.get(`/items/${id}`).then((r) => r.data),
  updateItem: (id, payload) => client.patch(`/items/${id}`, payload).then((r) => r.data),
  deleteItem: (id) => client.delete(`/items/${id}`).then((r) => r.data),
  taxonomy: () => client.get("/taxonomy").then((r) => r.data),
  swapImages: (id, a, b) => client.post(`/items/${id}/images/swap`, { a, b }).then((r) => r.data),
  reEnhance: (id) => client.post(`/items/${id}/images/re-enhance`, null, { timeout: 120000 }).then((r) => r.data),
  // Task 6 - suit sets
  listSets: () => client.get("/sets").then((r) => r.data),
  createSet: (item_ids, name) => client.post("/sets", { item_ids, name }).then((r) => r.data),
  dissolveSet: (setId) => client.delete(`/sets/${setId}`).then((r) => r.data),
  unlinkFromSet: (setId, itemId) => client.post(`/sets/${setId}/unlink/${itemId}`).then((r) => r.data),
  addItemPhoto: (id, slot, image) => client.post(`/items/${id}/photos/${slot}`, { image }, { timeout: 60000 }).then((r) => r.data),
  reclassify: () => client.post("/items/reclassify", null, { timeout: 120000 }).then((r) => r.data),
  bulkImport: (file, enhance) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("enhance", enhance ? "true" : "false");
    return client.post("/items/bulk", fd, { timeout: 300000 }).then((r) => r.data);
  },
  bulkStatus: (jobId) => client.get(`/items/bulk/${jobId}`).then((r) => r.data),
  bulkStart: (enhance, expected) => {
    const fd = new FormData();
    fd.append("enhance", enhance ? "true" : "false");
    fd.append("expected", String(expected || 0));
    return client.post("/items/bulk/start", fd).then((r) => r.data);
  },
  bulkAddPhotos: (jobId, files) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f, f.name));
    return client.post(`/items/bulk/${jobId}/photos`, fd, { timeout: 120000 }).then((r) => r.data);
  },
  bulkFinish: (jobId) => client.post(`/items/bulk/${jobId}/finish`).then((r) => r.data),
  bulkRetry: (jobId) => client.post(`/items/bulk/${jobId}/retry`).then((r) => r.data),
  bulkActive: () => client.get("/items/bulk/active").then((r) => r.data),
  bulkRegroup: (jobId) => client.post(`/items/bulk/${jobId}/regroup`).then((r) => r.data),
  listReview: () => client.get("/items/review").then((r) => r.data),
  resolveReview: (id, action) => client.post(`/items/${id}/review`, { action }).then((r) => r.data),
  estimateRetail: () => client.post("/items/estimate-retail", {}, { timeout: 120000 }).then((r) => r.data),
  autofillBackfill: (force = false) => client.post("/items/autofill/backfill", null, { params: { force }, timeout: 600000 }).then((r) => r.data),
  acceptSuggestion: (id, field) => client.post(`/items/${id}/accept-suggestion`, null, { params: { field } }).then((r) => r.data),
  // settings
  getSettings: () => client.get("/settings").then((r) => r.data),
  updateSettings: (payload) => client.put("/settings", payload).then((r) => r.data),
  // weather
  getWeather: (params) => client.get("/weather", { params }).then((r) => r.data),
  // outfits
  outfitToday: (payload) => client.post("/outfits/today", payload, { timeout: 120000 }).then((r) => r.data),
  // Task 10 - session look for today (existing generator; never repeats the last 7)
  dailyOutfit: (payload) => client.post("/outfits/daily", payload, { timeout: 180000 }).then((r) => r.data),
  dailyOutfitToday: () => client.get("/outfits/daily/today").then((r) => r.data),
  outfitWeek: (payload) => client.post("/outfits/week", payload, { timeout: 300000 }).then((r) => r.data),
  listOutfits: () => client.get("/outfits").then((r) => r.data),
  renderOutfit: (id, view = "front", who = "model") => client.post(`/outfits/${id}/render`, null, { params: { view, who }, timeout: 120000 }).then((r) => r.data),
  renderedLooks: (who = "me") => client.get("/outfits/rendered", { params: { who } }).then((r) => r.data),
  renderSpin: (id, who = "model") => client.post(`/outfits/${id}/spin`, null, { params: { who }, timeout: 180000 }).then((r) => r.data),
  outfitLookUrl: (id, view = "front", who = "model") => withTok(`${API}/outfits/${id}/look?view=${view}&who=${who}`),
  getProfile: () => client.get("/profile").then((r) => r.data),
  updateProfile: (payload) => client.put("/profile", payload, { timeout: 90000 }).then((r) => r.data),
  profileImageUrl: (kind) => withTok(`${API}/profile/image/${kind}`),
  twinPreview: (force = false) => client.post("/profile/twin-preview", null, { params: { force }, timeout: 180000 }).then((r) => r.data),
  twinPreviewUrl: () => withTok(`${API}/profile/twin-preview/image`),
  // notifications
  notifications: () => client.get("/notifications").then((r) => r.data),
  notificationsUnread: () => client.get("/notifications/unread-count").then((r) => r.data),
  notificationsRead: (ids) => client.post("/notifications/read", ids === "all" ? { all: true } : { ids }).then((r) => r.data),
  // lookbook: favorites, manual looks, swaps, daylists
  favoriteOutfit: (id) => client.post(`/outfits/${id}/favorite`).then((r) => r.data),
  favorites: () => client.get("/outfits/favorites").then((r) => r.data),
  createManualOutfit: (payload) => client.post("/outfits/manual", payload).then((r) => r.data),
  buildLook: (payload) => client.post("/outfits/build", payload, { timeout: 120000 }).then((r) => r.data),
  updateOutfit: (id, payload) => client.patch(`/outfits/${id}`, payload).then((r) => r.data),
  swapOutfitItem: (id, remove_item_id, add_item_id) => client.post(`/outfits/${id}/swap`, { remove_item_id, add_item_id }).then((r) => r.data),
  deleteOutfit: (id) => client.delete(`/outfits/${id}`).then((r) => r.data),
  daylists: () => client.get("/daylists").then((r) => r.data),
  createDaylist: (payload) => client.post("/daylists", payload).then((r) => r.data),
  updateDaylist: (id, payload) => client.patch(`/daylists/${id}`, payload).then((r) => r.data),
  deleteDaylist: (id) => client.delete(`/daylists/${id}`).then((r) => r.data),
  createTrip: (payload) => client.post("/trips", payload, { timeout: 150000 }).then((r) => r.data),
  listTrips: () => client.get("/trips").then((r) => r.data),
  getTrip: (id) => client.get(`/trips/${id}`).then((r) => r.data),
  updateTrip: (id, payload) => client.patch(`/trips/${id}`, payload).then((r) => r.data),
  deleteTrip: (id) => client.delete(`/trips/${id}`).then((r) => r.data),
  scanReceipt: (image) => client.post("/receipts", { image }, { timeout: 120000 }).then((r) => r.data),
  applyReceipt: (id, payload) => client.post(`/receipts/${id}/apply`, payload).then((r) => r.data),
  // wearlog
  createWearLog: (payload) => client.post("/wearlog", payload).then((r) => r.data),
  listWearLog: () => client.get("/wearlog").then((r) => r.data),
  deleteWearLog: (id) => client.delete(`/wearlog/${id}`).then((r) => r.data),
  // stats
  getStats: () => client.get("/stats").then((r) => r.data),
  draftListing: (id) => client.post(`/items/${id}/draft-listing`, null, { timeout: 60000 }).then((r) => r.data),
  fromFitPic: (image, enhance = false) => client.post("/items/from-fitpic", { image, enhance }, { timeout: 300000 }).then((r) => r.data),
  badges: () => client.get("/badges").then((r) => r.data),
  weeklyRecap: () => client.get("/recap/weekly").then((r) => r.data),
  worthHistory: (days = 90) => client.get("/worth/history", { params: { days } }).then((r) => r.data),
  streak: () => client.get("/streak").then((r) => r.data),
  showcase: () => client.get("/public/showcase").then((r) => r.data),
  // marketplaces (eBay etc) -> sold -> label -> email -> balance
  marketplaces: () => client.get("/marketplaces").then((r) => r.data),
  connectMarketplace: (id) => client.post(`/marketplaces/${id}/connect`).then((r) => r.data),
  disconnectMarketplace: (id) => client.delete(`/marketplaces/${id}`).then((r) => r.data),
  setShipFrom: (payload) => client.put("/marketplaces/ship-from", payload).then((r) => r.data),
  listExternal: (itemId, providers = ["ebay"]) => client.post(`/items/${itemId}/list-external`, { providers }, { timeout: 60000 }).then((r) => r.data),
  externalListings: () => client.get("/listings/external").then((r) => r.data),
  endExternalListing: (id) => client.delete(`/listings/external/${id}`).then((r) => r.data),
  simulateSale: (id) => client.post(`/listings/external/${id}/simulate-sale`, null, { timeout: 60000 }).then((r) => r.data),
  markShipped: (id) => client.post(`/listings/external/${id}/shipped`).then((r) => r.data),
  labelUrl: (shipmentId) => `${API}/shipments/${shipmentId}/label`,
  balance: () => client.get("/balance").then((r) => r.data),
  // closet review ("final pass")
  reviewQueue: () => client.get("/review/queue").then((r) => r.data),
  reviewScan: (payload = {}) => client.post("/review/scan", payload).then((r) => r.data),
  reviewScanStatus: (id) => client.get(`/review/scan/${id}`).then((r) => r.data),
  reviewRepair: (payload = {}) => client.post("/review/repair", payload).then((r) => r.data),
  reviewRepairActive: () => client.get("/review/repair/active").then((r) => r.data),
  reviewSwatches: (id) => client.get(`/review/items/${id}/swatches`).then((r) => r.data),
  reviewItem: (id, payload) => client.post(`/review/items/${id}`, payload).then((r) => r.data),
  reviewWashed: (id) => client.post(`/review/items/${id}/washed`).then((r) => r.data),
  reviewRephoto: (id, image) => client.post(`/review/items/${id}/rephoto`, { image }, { timeout: 180000 }).then((r) => r.data),
  reviewPolish: (id, color) => client.post(`/review/items/${id}/polish`, color ? { color } : {}, { timeout: 240000 }).then((r) => r.data),
  reviewPolishDecision: (id, decision) => client.post(`/review/items/${id}/polish-decision`, { decision }).then((r) => r.data),
  // "See it on my twin" for market / social pieces that fit me (explicit tap, cached per viewer + piece)
  tryOnStatus: (itemId) => client.get(`/market/items/${itemId}/try-on`).then((r) => r.data),
  tryOnRender: (itemId, force = false) => client.post(`/market/items/${itemId}/try-on`, null, { params: { force }, timeout: 180000 }).then((r) => r.data),
  tryOnImageUrl: (itemId) => withTok(`${API}/market/items/${itemId}/try-on/image`),
  resolveDuplicate: (id, payload) => client.post(`/review/duplicates/${id}`, payload).then((r) => r.data),
  bulkIntent: (item_ids, intent, wear_likelihood) => client.post("/review/bulk/intent", { item_ids, intent, wear_likelihood }).then((r) => r.data),
  bulkList: (item_ids, mode = "sell", price_strategy = "worth", sale_rack = null) => client.post("/review/bulk/list", { item_ids, mode, price_strategy, sale_rack }).then((r) => r.data),
  reviewTips: (force = false) => client.post(`/review/tips${force ? "?force=true" : ""}`, null, { timeout: 60000 }).then((r) => r.data),
  photoCheck: (image, slot = "front") => client.post("/review/photo-check", { image, slot }, { timeout: 45000 }).then((r) => r.data),
  cashout: (amount) => client.post("/balance/cashout", amount ? { amount } : {}).then((r) => r.data),
  // auth
  authStatus: () => client.get("/auth/status").then((r) => r.data),
  register: (payload) => client.post("/auth/register", payload).then((r) => r.data),
  login: (payload) => client.post("/auth/login", payload).then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),
  demoLogin: () => client.post("/auth/demo").then((r) => r.data),
  demoReset: () => client.post("/auth/demo/reset", null, { timeout: 600000 }).then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  // social
  getMe: () => client.get("/users/me").then((r) => r.data),
  updateMe: (payload) => client.patch("/users/me", payload).then((r) => r.data),
  getUser: (handle) => client.get(`/users/${handle}`).then((r) => r.data),
  follow: (handle) => client.post(`/users/${handle}/follow`).then((r) => r.data),
  unfollow: (handle) => client.delete(`/users/${handle}/follow`).then((r) => r.data),
  people: (q) => client.get("/people", { params: q ? { q } : {} }).then((r) => r.data),
  feed: (scope = "all") => client.get("/feed", { params: { scope } }).then((r) => r.data),
  createPost: (payload) => client.post("/posts", payload).then((r) => r.data),
  deletePost: (id) => client.delete(`/posts/${id}`).then((r) => r.data),
  toggleLike: (id) => client.post(`/posts/${id}/like`).then((r) => r.data),
  react: (id, emoji) => client.post(`/posts/${id}/react`, { emoji }).then((r) => r.data),
  comments: (id) => client.get(`/posts/${id}/comments`).then((r) => r.data),
  addComment: (id, text) => client.post(`/posts/${id}/comments`, { text }).then((r) => r.data),
  market: (params) => client.get("/market", { params }).then((r) => r.data),
  marketForYou: (params) => client.get("/market/for-you", { params }).then((r) => r.data),
  peopleSuggested: () => client.get("/people/suggested").then((r) => r.data),
  peopleFollowing: () => client.get("/people/following").then((r) => r.data),
  mySizes: () => client.get("/market/my-sizes").then((r) => r.data),
  tradeMatches: (itemId, fit = false) => client.get(`/market/trade-matches/${itemId}`, { params: { fit } }).then((r) => r.data),
  myListings: () => client.get("/listings/mine").then((r) => r.data),
  setListingType: (item_ids, listing_type) => client.post("/listings/bulk", { item_ids, listing_type }).then((r) => r.data),
  makeOffer: (payload) => client.post("/offers", payload).then((r) => r.data),
  offers: (box = "inbox") => client.get("/offers", { params: { box } }).then((r) => r.data),
  respondOffer: (id, action) => client.post(`/offers/${id}/respond`, { action }).then((r) => r.data),
  offersPending: () => client.get("/offers/unread-count").then((r) => r.data),
  offerMessages: (id) => client.get(`/offers/${id}/messages`).then((r) => r.data),
  sendOfferMessage: (id, payload) => client.post(`/offers/${id}/messages`, payload).then((r) => r.data),
};

export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const money = (v) => {
  if (v === null || v === undefined) return "—";
  return "$" + Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
};

export const CATEGORIES = ["top", "bottom", "outerwear", "dress", "shoes", "accessory", "other"];
export const SEASONS = ["spring", "summer", "fall", "winter"];
export const FORMALITIES = ["casual", "smart-casual", "business", "formal", "athletic"];
